import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #busy = false;
  #requestPending = false;
  #requiresReview = false;
  #checkoutState;
  #formState;
  #acceleratedState = [];
  #status;

  #messages = JSON.parse(document.getElementById('bape-candyrack-cart-config')?.textContent || '{}');

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
    this.addEventListener('submit', this.#guardCheckout, true);
    this.addEventListener('click', this.#guardCheckout, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#queueQuantityChange);
    this.removeEventListener('submit', this.#guardCheckout, true);
    this.removeEventListener('click', this.#guardCheckout, true);
  }

  #queueQuantityChange = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target) || !event.detail.cartLine) return;
    if (this.#requiresReview) return this.#reviewCart();
    if (this.#requestPending) return;
    // Cover the debounce interval as well as the request itself.
    this.#disableCartItems();
    this.#debouncedOnChange(event);
  };

  #guardCheckout = (event) => {
    if (event.type === 'click' && !event.target.closest?.('[name="checkout"]')) return;
    if (!this.#busy && !this.#requiresReview) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#requiresReview) this.#reviewCart();
  };

  #reviewCart() {
    const url = new URL(Theme.routes.cart_url, window.location.href);
    url.searchParams.set('cart_recovered', '1');
    window.location.assign(url.href);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) {
      // A section refresh can replace the input during debounce. No request
      // will own the pending state in that case, so release it here.
      if (!this.#requestPending) this.#enableCartItems();
      return;
    }

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    // Keep the row (including the last row and bundle children) until Shopify
    // confirms the removal. A failed request must not hide an item being sold.
    return this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    if (this.#requiresReview) return this.#reviewCart();
    if (this.#requestPending) return;
    this.#requestPending = true;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const controller = new AbortController();
    let timeout;
    try {
      // Bound both the response headers and body, including fetch wrappers
      // that do not propagate AbortSignal. Late responses cannot update the UI.
      const { response, parsedResponseText } = await Promise.race([
        (async () => {
          const response = await fetch(Theme.routes.cart_change_url, {
            ...fetchConfig('json', { body }), signal: controller.signal,
          });
          return { response, parsedResponseText: JSON.parse(await response.text()) };
        })(),
        new Promise((_, reject) => {
          timeout = setTimeout(() => {
            reject(new Error('Cart update timed out'));
            controller.abort();
          }, 10000);
        }),
      ]);

      resetShimmer(this);

      if (parsedResponseText.errors && response.status === 422) {
        this.#handleCartError(line, parsedResponseText);
        return;
      }

      if (!response.ok || typeof parsedResponseText.sections?.[this.sectionId] !== 'string') {
        throw new Error('Cart update could not be confirmed');
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );
      if (!newSectionHTML.querySelector('cart-items-component')) {
        throw new Error('Cart section missing from response');
      }

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full', { injectStylesheet: true });

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      // The write may have reached Shopify even if its response was lost.
      // Require a fresh server-rendered review before another edit/checkout;
      // never retry an ambiguous removal or submit stale quantity inputs.
      this.#requiresReview = true;
      for (const selector of this.refs.quantitySelectors || []) {
        const input = selector.querySelector('input');
        if (input) input.value = input.defaultValue;
      }
      console.error('Cart update failed', error);
    } finally {
      clearTimeout(timeout);
      resetShimmer(this);
      this.#requestPending = false;
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, 'full', { injectStylesheet: true });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    if (this.#busy) return;
    this.#busy = true;
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
    const button = this.querySelector('[name="checkout"]');
    if (button) {
      this.#checkoutState = { button, disabled: button.disabled, label: (button.querySelector('.button-text') || button).textContent };
      button.disabled = true;
      (button.querySelector('.button-text') || button).textContent = this.#messages.checkoutUpdating || 'Updating your cart…';
    }
    const form = this.querySelector('.cart-form');
    if (form) {
      this.#formState = { form, inert: form.inert };
      form.inert = true;
    }
    this.#acceleratedState = [...this.querySelectorAll('.additional-checkout-buttons')].map((element) => {
      const state = { element, inert: element.inert };
      element.inert = true;
      return state;
    });
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.#busy = false;
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
    if (this.#checkoutState) {
      const { button, disabled, label } = this.#checkoutState;
      button.disabled = disabled;
      (button.querySelector('.button-text') || button).textContent = label;
    }
    if (this.#formState) this.#formState.form.inert = this.#formState.inert;
    for (const { element, inert } of this.#acceleratedState) element.inert = inert;
    this.#acceleratedState = [];
    this.#checkoutState = undefined;
    this.#formState = undefined;
    if (!this.#requiresReview) return;

    const reviewLabel = this.#messages.checkoutReview || 'Review cart';
    const button = this.querySelector('[name="checkout"]');
    if (button) {
      button.disabled = false;
      (button.querySelector('.button-text') || button).textContent = reviewLabel;
    }
    // Keep stale line indexes/quantities out of further writes. Navigation and
    // the review control stay available even when the cart endpoint is down.
    const form = this.querySelector('.cart-form');
    if (form) form.inert = true;
    this.querySelectorAll('.additional-checkout-buttons').forEach((element) => { element.inert = true; });
    this.#status?.remove();
    this.#status = document.createElement('p');
    this.#status.setAttribute('role', 'alert');
    this.#status.dataset.cartUpdateError = '';
    this.#status.textContent = this.#messages.checkoutRetry || 'We could not confirm your cart. Check your connection and try again, or review your cart.';
    const review = document.createElement('a');
    const url = new URL(Theme.routes.cart_url, window.location.href);
    url.searchParams.set('cart_recovered', '1');
    review.href = url.href;
    review.textContent = reviewLabel;
    this.#status.append(' ', review);
    if (button) button.before(this.#status);
    else this.prepend(this.#status);
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
